/**
 * Header eyebrow copy for the ExceptionDrawer - standalone so the drawer
 * slices can share it without importing the drawer file.
 */

// Per-exception-code object nouns. The data_domain fallback below is too
// coarse for domains that host mixed object types (accounting carries
// journals, purchase orders, register closures and COGS months; system
// carries registers, outlets, settings and staff accounts), so codes whose
// rows are NOT the domain's default object override here. Exported via
// ``codeNounEntries`` for the registry-guard test.
const CODE_NOUN: Record<string, string> = {
  'ex-manual-journal-on-cogs-base': 'accounting journal',
  'ex-inv-ls-unsent-po': 'purchase order',
  'ex-inv-ls-overdue-po': 'purchase order',
  'ex-inv-ls-pending-transfers': 'stock transfer',
  'ex-inv-ls-xero-account-error': 'product',
  'ex-cogs-monthly-incomplete': 'COGS month',
  'xero-coa-alignment': 'account',
  'ex-register-closure-missing-in-xero': 'register closure',
  'ex-register-closure-duplicate-bill': 'register closure',
  'ex-register-low-closure-rate': 'register',
  'ex-outlet-ls-register-open-too-long': 'register',
  'ex-outlet-ls-register-prefix-incorrect': 'register',
  'ex-outlet-ls-address-incorrect': 'outlet',
  'ex-outlet-ls-dnu': 'outlet',
  'ex-ls-loyalty-enabled': 'setting',
  'ex-ls-ecommerce-enabled': 'setting',
  'ex-user-ls-unapproved-domain': 'staff account',
  'ex-product-ls-category-alignment': 'category',
}

/** Read-only view of the per-code noun map (for the registry-guard test). */
export const codeNounEntries = (): ReadonlyArray<[string, string]> =>
  Object.entries(CODE_NOUN)

// Object noun for the eyebrow + empty-state copy. The exception code wins
// (mixed-object domains); otherwise derived from the data domain so copy
// reads naturally ("issues on this sale / product").
export const nounForDomain = (
  dataDomain?: string,
  exceptionCode?: string,
): string => {
  if (exceptionCode && CODE_NOUN[exceptionCode]) return CODE_NOUN[exceptionCode]
  if (dataDomain === 'sale') return 'sale'
  if (dataDomain === 'client') return 'customer'
  if (dataDomain?.startsWith('product') || dataDomain === 'stock_management')
    return 'product'
  return 'record'
}

// Header eyebrow: "{OBJECT} EXCEPTIONS" (e.g. "PRODUCT EXCEPTIONS"). Identical
// for every role - the drawer is the object's exception surface, not a verb.
export const buildEyebrow = (
  dataDomain: string | undefined,
  exceptionCode?: string,
): string => `${nounForDomain(dataDomain, exceptionCode).toUpperCase()} EXCEPTIONS`
